import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { isArray } from 'lodash';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import {
  CONTACT_EMAIL_TYPES,
  CONTACT_NUMBER_TYPES,
  REFERENCE_MAPPINGS,
  SALUTATIONS,
} from '@shared/constants';
import {
  ICityReference,
  IContactAddress,
  IContactEmail,
  IContactNumber,
  IContactNumberPopupConfig,
  IName,
  INameRequest,
  IReference,
  IReferenceMappingSetupResponse,
  MultiFieldData,
} from '@shared/interfaces';
import { emailValidator } from '@shared/utils';

import { noWhitespaceValidator } from '../../../../core/helpers/form-field-white-space-validator';
import { AddressReferenceService } from '../../../../references/services';
import { ReferenceMappingService } from '../../../../setup/reference-mapping/services/reference-mapping.service';
import { IPhoneNumberInputChange } from '../../../../shared/components/phone-number-input/types';
import { ELAutocompleteElement } from '@el-autocomplete';

interface DropdownsListArray {
  statesListForDropdown: ELAutocompleteElement[];
  districtsListForDropdown: ELAutocompleteElement[];
  citiesListForDropdown: ELAutocompleteElement[];
  postalCodesListForDropdown: ELAutocompleteElement[];
}

type IContactNumberFormGroup = FormGroup<{
  is_primary: FormControl<boolean>;
  type: FormControl<CONTACT_NUMBER_TYPES>;
  phone_number: FormControl<IPhoneNumberInputChange>;
}>;

type IContactEmailFormGroup = FormGroup<{
  is_primary: FormControl<boolean>;
  type: FormControl<CONTACT_EMAIL_TYPES>;
  email: FormControl<string>;
}>;

export type IAddressFormGroup = FormGroup<{
  is_primary: FormControl<boolean>;
  address: FormGroup<{
    line1: FormControl<string>;
    line2: FormControl<string>;
    line3: FormControl<string>;
    country: FormControl<string>;
    state: FormControl<string>;
    district: FormControl<string>;
    city: FormControl<string>;
    postal_code: FormControl<string>;
  }>;
}>;

@Component({
  selector: 'el-inline-popup',
  templateUrl: './inline-popup.component.html',
  styleUrls: ['./inline-popup.component.scss'],
})
export class InlinePopupComponent implements OnInit, OnDestroy {
  @Input() data: MultiFieldData;
  @Input() noOfFields: number;
  @Input() popupConfig?: IContactNumberPopupConfig;
  @Input() isRequired = false;
  @Output() onSetValue = new EventEmitter<MultiFieldData>();

  private readonly onDestroy$ = new Subject<void>();

  // ─── PHONE ────────────────────────────────────────────────────────────────
  CONTACT_NUMBER_TYPES = CONTACT_NUMBER_TYPES;
  contactNumbers: IContactNumberFormGroup[] = [];

  // ─── EMAIL ────────────────────────────────────────────────────────────────
  CONTACT_EMAIL_TYPES = CONTACT_EMAIL_TYPES;
  contactEmails: IContactEmailFormGroup[] = [];

  // ─── ADDRESS ──────────────────────────────────────────────────────────────
  addresses: IAddressFormGroup[] = [];
  countryListForDropDown: ELAutocompleteElement[] = [];
  countryListMapping: IReferenceMappingSetupResponse;
  stateListMapping: IReferenceMappingSetupResponse;
  districtListMapping: IReferenceMappingSetupResponse;
  cityListMapping: IReferenceMappingSetupResponse;
  postalCodeListMapping: IReferenceMappingSetupResponse;
  referenceMappings: any;
  loadingStates = false;
  dropdownsList: DropdownsListArray[] = [];
  loadingDistricts = false;
  loadingCities = false;
  loadingPostalCodes = false;

  // ─── NAME ─────────────────────────────────────────────────────────────────
  salutations = Object.values(SALUTATIONS);
  form = new FormGroup({
    salutation: new FormControl('', Validators.required),
    first_name: new FormControl('', Validators.required),
    last_name: new FormControl('', Validators.required),
    middle_name: new FormControl(''),
  });

  constructor(
    private readonly referenceMappingService: ReferenceMappingService,
    private readonly addressReferenceService: AddressReferenceService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.setValuesToMultiFields(this.data.type);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  async setValuesToMultiFields(type: string): Promise<void> {
    switch (type) {
      case 'PHONE':
        if (
          this.data.value &&
          (this.data.value as IContactNumber[]).length > 0
        ) {
          (this.data.value as IContactNumber[]).forEach((element) => {
            this.contactNumbers?.push(
              this.generateContactNumberFormGroup(
                element,
                this.popupConfig?.block_default
              )
            );
          });
        } else {
          this.addNewContact(false, undefined);
          this.contactNumbers?.[0]?.get('is_primary')?.setValue(true);
        }
        break;

      case 'EMAIL':
        if (
          this.data.value &&
          (this.data.value as IContactEmail[]).length > 0
        ) {
          (this.data.value as IContactEmail[]).forEach((element) => {
            this.contactEmails?.push(
              this.generateContactEmailFormGroup(element)
            );
          });
        } else {
          this.addNewContactEmail();
          this.contactEmails?.[0]?.get('is_primary')?.setValue(true);
        }
        break;

      case 'ADDRESS':
        this.getReferenceMapping()
          .then((data) => {
            this.referenceMappings = data;
            this.initReferenceMapping();
            if (
              this.data.value &&
              (this.data.value as IContactAddress[])?.length > 0
            ) {
              (this.data.value as IContactAddress[])?.forEach(
                (element, index) => {
                  const stateForDropdown: ELAutocompleteElement[] = [
                    {
                      value: element.address.stateData?._id.toString(),
                      displayValue: element.address.stateData?.name,
                      originalData: element.address.stateData,
                    },
                  ];
                  const districtForDropdown: ELAutocompleteElement[] = [
                    {
                      value: element.address.districtData._id.toString(),
                      displayValue: element.address.districtData.name,
                      originalData: element.address.districtData,
                    },
                  ];
                  const cityForDropdown: ELAutocompleteElement[] = [
                    {
                      value: element.address.cityData._id.toString(),
                      displayValue: element.address.cityData.name,
                      originalData: element.address.cityData,
                    },
                  ];
                  this.addresses.push(this.generateAddressFormGroup(element));
                  this.dropdownsList.push({
                    citiesListForDropdown: cityForDropdown,
                    districtsListForDropdown: districtForDropdown,
                    postalCodesListForDropdown: [],
                    statesListForDropdown: stateForDropdown,
                  });

                  this.getFormControl(index, 'country').setValue(
                    element?.address?.country || ''
                  );
                  this.getFormControl(index, 'city').setValue(
                    element?.address?.city || ''
                  );
                  this.getFormControl(index, 'state').setValue(
                    element?.address?.state || ''
                  );
                  this.getFormControl(index, 'district').setValue(
                    element?.address?.district || ''
                  );
                }
              );
            } else {
              this.addNewAddresses();
              this.addresses[0]?.get('is_primary')?.setValue(true);
            }
          })
          .catch((error) => console.log(error));
        break;

      case 'NAME':
        if (this.data.value) {
          const name = this.data.value as IName;
          this.form.setValue({
            salutation: name?.salutation ?? '',
            first_name: name?.first_name ?? '',
            last_name: name?.last_name ?? '',
            middle_name: name?.middle_name ?? '',
          });
        }

        this.form.controls.first_name.valueChanges
          .pipe(takeUntil(this.onDestroy$), debounceTime(50))
          .subscribe((firstName) => {
            this.form.controls.first_name.setValue(
              this.capitalizeFirstLetter(firstName),
              { emitEvent: false }
            );
          });

        this.form.controls.last_name.valueChanges
          .pipe(takeUntil(this.onDestroy$), debounceTime(50))
          .subscribe((lastName) => {
            this.form.controls.last_name.setValue(
              this.capitalizeFirstLetter(lastName),
              { emitEvent: false }
            );
          });

        this.form.controls.middle_name.valueChanges
          .pipe(takeUntil(this.onDestroy$), debounceTime(50))
          .subscribe((middleName) => {
            this.form.controls.middle_name.setValue(
              this.capitalizeFirstLetter(middleName),
              { emitEvent: false }
            );
          });

        this.form.valueChanges.subscribe((value) => {
          this.onSetValue.emit({
            type: 'NAME',
            value: value as INameRequest,
            fieldConfig: this.data.fieldConfig,
            validForm: this.form.valid,
          });
        });
        break;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PHONE methods
  // ═══════════════════════════════════════════════════════════════════════════

  addNewContact(primary_disabled: boolean, number?: IContactNumber) {
    const formGroup = this.generateContactNumberFormGroup(
      number,
      primary_disabled
    );
    formGroup.valueChanges.subscribe(() => {
      this.emitContactNumbers();
    });
    this.contactNumbers.push(formGroup);
    this.emitContactNumbers();
  }

  onAddPhoneNumber() {
    this.addNewContact(false, undefined);
  }

  onDeleteClicked(index: number) {
    this.contactNumbers?.splice(index, 1);
  }

  radioChange(index: number) {
    if (!this.contactNumbers?.[index]?.get('is_primary')?.value) {
      this.contactNumbers?.forEach((_controls, _index) => {
        _controls?.get('is_primary').setValue(index === _index);
      });
    }
  }

  handleOnChangePhoneNumber(
    event: IPhoneNumberInputChange,
    index: number
  ): void {
    const phoneNumberControl = this.contactNumbers[index].get('phone_number');
    phoneNumberControl.setValue(event);
    phoneNumberControl.markAsDirty();
    phoneNumberControl.markAsTouched();
  }

  private emitContactNumbers() {
    const values = this.contactNumbers.map(
      (group) => group.value as IContactNumber
    );
    const isValid = this.contactNumbers.every((group) => group.valid);
    this.onSetValue.emit({
      type: 'PHONE',
      value: isValid ? values : null,
      fieldConfig: this.data.fieldConfig,
    });
  }

  private generateContactNumberFormGroup(
    number?: IContactNumber,
    primary_disabled = false
  ): IContactNumberFormGroup {
    return new FormGroup({
      is_primary: new FormControl({
        value: number?.is_primary || false,
        disabled: primary_disabled,
      }),
      type: new FormControl(number?.type, [Validators.required]),
      phone_number: new FormControl(
        {
          ...number?.phone_number,
          valid: !!number?.phone_number,
        },
        [Validators.required, this.phoneNumberValidator]
      ),
    });
  }

  private readonly phoneNumberValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const value = control.value as IPhoneNumberInputChange;
    if (!value) return { required: true };
    if (!value.valid) return { invalidPhone: true };
    return null;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EMAIL methods
  // ═══════════════════════════════════════════════════════════════════════════

  addNewContactEmail() {
    const formGroup = this.generateContactEmailFormGroup();
    formGroup.valueChanges.subscribe(() => {
      this.emitContactEmails();
    });
    this.contactEmails.push(formGroup);
    this.emitContactEmails();
  }

  onEmailDeleteClicked(index: number) {
    this.contactEmails?.splice(index, 1);
  }

  private emitContactEmails() {
    const values = this.contactEmails.map(
      (group) => group.value as IContactEmail
    );
    const isValid = this.contactEmails.every((group) => group.valid);
    this.onSetValue.emit(
      isValid
        ? {
          type: 'EMAIL',
          value: values,
          fieldConfig: this.data.fieldConfig,
        }
        : null
    );
  }

  private generateContactEmailFormGroup(
    email?: IContactEmail
  ): IContactEmailFormGroup {
    return new FormGroup({
      is_primary: new FormControl(email?.is_primary || false),
      type: new FormControl(email?.type, [Validators.required]),
      email: new FormControl(email?.email || '', [
        Validators.required,
        emailValidator(),
      ]),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ADDRESS methods
  // ═══════════════════════════════════════════════════════════════════════════

  addNewAddresses() {
    this.dropdownsList.push({
      statesListForDropdown: [],
      districtsListForDropdown: [],
      citiesListForDropdown: [],
      postalCodesListForDropdown: [],
    });
    const formGroup = this.generateAddressFormGroup();
    formGroup.valueChanges.subscribe((value) => {
      if (this.isValidAddress(value)) {
        this.emitAddresses();
      }
    });
    this.addresses.push(formGroup);
    if (this.addresses.length === 1) {
      this.addresses[0].get('is_primary')?.setValue(true);
    }
    if (this.isValidAddress(formGroup.value)) {
      this.emitAddresses();
    }
  }

  onAddressDeleteClicked(index: number) {
    this.addresses.splice(index, 1);
    this.dropdownsList.splice(index, 1);
  }

  private emitAddresses() {
    const values = this.addresses.map(
      (group) => group.value as IContactAddress
    );
    const isValid = this.addresses.every((group) =>
      this.isValidAddress(group.value)
    );
    this.onSetValue.emit({
      type: 'ADDRESS',
      value: isValid ? values : null,
      fieldConfig: this.data.fieldConfig,
    });
  }

  isValidAddress(value: any): boolean {
    return (
      value.address.line1 &&
      value.address.country &&
      value.address.district &&
      value.address.city &&
      value.address.postal_code
    );
  }

  private generateAddressFormGroup(
    address?: IContactAddress
  ): IAddressFormGroup {
    return new FormGroup({
      is_primary: new FormControl(!!address?.is_primary),
      address: new FormGroup({
        line1: new FormControl(address?.address?.line1, [
          Validators.required,
          noWhitespaceValidator,
        ]),
        line2: new FormControl(address?.address?.line2),
        line3: new FormControl(address?.address?.line3),
        country: new FormControl(address?.address?.country, [
          Validators.required,
        ]),
        state: new FormControl(address?.address?.state, [Validators.required]),
        district: new FormControl(address?.address?.district, [
          Validators.required,
        ]),
        city: new FormControl(address?.address?.city, [Validators.required]),
        postal_code: new FormControl(
          { value: address?.address?.postal_code, disabled: true },
          [Validators.required]
        ),
      }),
    });
  }

  initReferenceMapping() {
    this.countryListMapping = this.referenceMappings.countryListMappingData;
    this.stateListMapping = this.referenceMappings.stateListMappingData;
    this.districtListMapping = this.referenceMappings.districtListMappingData;
    this.cityListMapping = this.referenceMappings.cityListMappingData;
    this.postalCodeListMapping =
      this.referenceMappings.postalCodeListMappingData;
    this.countryListForDropDown =
      this.referenceMappings.countryListForDropDownData;
  }

  async updateCountryValues(
    e: ELAutocompleteElement | ELAutocompleteElement[],
    i: number
  ) {
    if (!e || isArray(e)) return;
    const currentValue = this.getFormControl(i, 'country')?.value;
    if (e.value !== currentValue) {
      this.loadingStates = true;
      this.setValueToAddressFields(i, 'country', e.value);
      this.clearAddressFields(i, 'state');
      this.clearAddressFields(i, 'district');
      this.clearAddressFields(i, 'city');
      this.clearAddressFields(i, 'postal_code');
      this.dropdownsList[i].statesListForDropdown =
        await this.addressReferenceService.getStatesToAddressPopup(e.value);
      this.dropdownsList[i].districtsListForDropdown = [];
      this.dropdownsList[i].citiesListForDropdown = [];
      this.dropdownsList[i].postalCodesListForDropdown = [];
      this.loadingStates = false;
    }
  }

  async updateDropdownOnFocus(entity: string, index: number) {
    if (entity === 'state') {
      this.loadingStates = true;
      this.dropdownsList[index].statesListForDropdown =
        await this.addressReferenceService.getStatesToAddressPopup(
          this.getFormControl(index, 'country').value
        );
      this.loadingStates = false;
    } else if (entity === 'district') {
      this.loadingDistricts = true;
      this.dropdownsList[index].districtsListForDropdown =
        await this.addressReferenceService.getDistrictsToAddressPopup(
          this.getFormControl(index, 'state').value
        );
      this.loadingDistricts = false;
    } else if (entity === 'city') {
      this.loadingCities = true;
      this.dropdownsList[index].citiesListForDropdown =
        await this.addressReferenceService.getCitiesToAddressPopup(
          this.getFormControl(index, 'district').value
        );
      this.loadingCities = false;
    }
  }

  async updateStateValues(
    e: ELAutocompleteElement | ELAutocompleteElement[],
    i: number
  ) {
    if (!e || isArray(e)) return;
    const currentValue = this.getFormControl(i, 'state')?.value;
    if (e.value !== currentValue) {
      this.loadingStates = true;
      this.setValueToAddressFields(i, 'state', e.value);
      this.clearAddressFields(i, 'district');
      this.clearAddressFields(i, 'city');
      this.clearAddressFields(i, 'postal_code');
      this.dropdownsList[i].districtsListForDropdown =
        await this.addressReferenceService.getDistrictsToAddressPopup(e.value);
      this.dropdownsList[i].citiesListForDropdown = [];
      this.dropdownsList[i].postalCodesListForDropdown = [];
      this.loadingStates = false;
    }
  }

  async updateDistrictValues(
    e: ELAutocompleteElement | ELAutocompleteElement[],
    i: number
  ) {
    if (!e || isArray(e)) return;
    const currentValue = this.getFormControl(i, 'district')?.value;
    if (e.value !== currentValue) {
      this.loadingDistricts = true;
      this.setValueToAddressFields(i, 'district', e.value);
      this.clearAddressFields(i, 'city');
      this.clearAddressFields(i, 'postal_code');
      this.dropdownsList[i].citiesListForDropdown =
        await this.addressReferenceService.getCitiesToAddressPopup(e.value);
      this.dropdownsList[i].postalCodesListForDropdown = [];
      this.loadingDistricts = false;
    }
  }

  async updateCityValues(
    e: ELAutocompleteElement | ELAutocompleteElement[],
    i: number
  ) {
    if (!e || isArray(e)) return;
    const currentValue = this.getFormControl(i, 'city')?.value;
    if (e.value !== currentValue) {
      this.loadingCities = true;
      this.loadingPostalCodes = true;
      this.setValueToAddressFields(i, 'city', e.value);
      const postalCode = (e.originalData as IReference<ICityReference>)
        ?.postal_code;
      this.setValueToAddressFields(i, 'postal_code', postalCode.toString());
      this.loadingCities = false;
      this.loadingPostalCodes = false;
    }
    if (this.getFormControl(i, 'city')?.value) {
      this.getFormControl(i, 'postal_code')?.enable();
    } else {
      this.getFormControl(i, 'postal_code')?.disable();
    }
  }

  private getFormControl(index: number, control: string) {
    return this.addresses[index]?.controls?.address?.get(
      control
    ) as FormControl<string>;
  }

  private setValueToAddressFields(
    index: number,
    control: string,
    value: string
  ) {
    this.getFormControl(index, control)?.setValue(value);
    this.getFormControl(index, control)?.markAsDirty();
    this.getFormControl(index, control)?.setErrors(null);
  }

  private clearAddressFields(index: number, control: string) {
    this.getFormControl(index, control)?.setValue(null);
    this.getFormControl(index, control)?.markAsUntouched();
    this.getFormControl(index, control)?.setErrors(null);
  }

  async getReferenceMapping() {
    const countryListMapping =
      await this.referenceMappingService.getMappingByKey(
        REFERENCE_MAPPINGS.ADDRESS_COUNTRY
      );
    const stateListMapping = await this.referenceMappingService.getMappingByKey(
      REFERENCE_MAPPINGS.ADDRESS_PROVINCE
    );
    const districtListMapping =
      await this.referenceMappingService.getMappingByKey(
        REFERENCE_MAPPINGS.ADDRESS_DISTRICT
      );
    const cityListMapping = await this.referenceMappingService.getMappingByKey(
      REFERENCE_MAPPINGS.ADDRESS_CITY
    );
    const postalCodeListMapping =
      await this.referenceMappingService.getMappingByKey(
        REFERENCE_MAPPINGS.ADDRESS_POSTAL_CODE
      );
    const countryListForDropDown =
      await this.addressReferenceService.getCountriesToAddressPopup();
    return {
      countryListMappingData: countryListMapping,
      stateListMappingData: stateListMapping,
      districtListMappingData: districtListMapping,
      cityListMappingData: cityListMapping,
      postalCodeListMappingData: postalCodeListMapping,
      countryListForDropDownData: countryListForDropDown,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NAME helpers
  // ═══════════════════════════════════════════════════════════════════════════

  private capitalizeFirstLetter(text: string): string {
    if (!text) return '';
    return text.toString().charAt(0).toUpperCase() + text.toString().slice(1);
  }
}
